import { useEffect, useMemo, useRef } from "react";
import {
  collection,
  getDocs,
  orderBy,
  limit,
  query,
  where,
  doc,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../../conf/firebaseConfig";
import { useAuth } from "../../context/AuthContext";
import { SMDConsult } from "../../typings";
import { useIonViewDidLeave } from "@ionic/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ConsultType } from "../../typings/enums";
import { ConsultVisitStatusEnum } from "@futurhealth/steadymd-api-client";
import { isMissedSlaReason } from "../../utils/medicationOrderUtils";

/**
 * Fetches the latest consultation document for a user from Firestore.
 *
 * @param userId - The unique identifier of the user
 * @param consultType - Array of consultation types to filter by (defaults to scheduled video visits)
 * @param includeCanceled - Whether to include canceled consultations in results (defaults to false)
 * @param includeRejectedInitialAsyncMissedSla - When true, do not filter out rejected initial_async consults with reason missed_sla (for Chat with Provider card)
 * @returns Promise resolving to the latest consultation or null if none found
 *
 * @example
 * // Get latest active consultation
 * const consult = await fetchLatestConsult('user123', [ConsultType.scheduled_video_visit]);
 *
 * @example
 * // Get latest consultation including canceled ones (for reschedule functionality)
 * const consultIncludingCanceled = await fetchLatestConsult('user123', [ConsultType.scheduled_video_visit], true);
 */
const fetchLatestConsult = async (
  userId: string,
  consultType = [ConsultType.scheduled_video_visit],
  includeCanceled = false,
  includeRejectedInitialAsyncMissedSla = false
): Promise<SMDConsult | null> => {
  const q = query(
    collection(db, "steadymd.consults"),
    where("userId", "==", userId),
    where("consultType", "in", consultType),
    orderBy("createdAt", "desc"),
    limit(10) // Fetch enough to handle multiple canceled/rejected consults
  );

  const docQuery = await getDocs(q);
  if (docQuery.empty) {
    return null;
  }

  if (includeCanceled) {
    // When includeCanceled is true, we want the latest consult that is either:
    // 1. Canceled/Rejected (for showing reschedule card), OR
    // 2. Any other status (for showing the active appointment)
    // We prioritize non-canceled/non-rejected consults if they exist
    const consults = docQuery.docs.map(
      (doc) => ({ ...doc.data() }) as SMDConsult
    );

    // First try to find a non-canceled, non-rejected consult
    // Filter out ALL rejected consults (not just specific reasons) to ensure
    // we return the new scheduled consult after rescheduling, even if there's
    // a rejected consult with "Missed Visit" or other reasons
    const nonCanceledConsult = consults.find(
      (c) =>
        c.visitStatus !== ConsultVisitStatusEnum.Canceled &&
        c.visitStatus !== ConsultVisitStatusEnum.Rejected
    );

    // If we found a non-canceled consult, return it. Otherwise return the latest (which is canceled/rejected)
    return nonCanceledConsult || consults[0];
  }

  // Filter out canceled consults and rejected consults (all rejected consults are terminal states).
  // Exception: when includeRejectedInitialAsyncMissedSla is true, keep rejected initial_async with reason missed_sla
  // so the hero card can show "Chat with Provider" for that case only.
  const nonCanceledConsults = docQuery.docs
    .map((doc) => ({ ...doc.data() }) as SMDConsult)
    .filter((consult) => {
      if (
        consult.visitStatus === ConsultVisitStatusEnum.Canceled ||
        consult.visitStatus === ConsultVisitStatusEnum.Rejected
      ) {
        const isRejectedMissedSla =
          includeRejectedInitialAsyncMissedSla &&
          consult.consultType === ConsultType.initial_async &&
          consult.visitStatus === ConsultVisitStatusEnum.Rejected &&
          isMissedSlaReason(consult);
        if (!isRejectedMissedSla) return false;
      }
      return true;
    });

  return nonCanceledConsults.length > 0 ? nonCanceledConsults[0] : null;
};

/**
 * React hook that provides real-time access to the latest consultation for a user.
 *
 * Automatically subscribes to real-time updates from Firestore and maintains query cache.
 * Supports both active-only consultations (default) and including canceled consultations
 * for specific use cases like reschedule functionality.
 *
 * @param consultType - Array of consultation types to filter by (defaults to scheduled video visits)
 * @param includeCanceled - Whether to include canceled consultations (defaults to false)
 * @param includeRejectedInitialAsyncMissedSla - When true, include rejected initial_async with reason missed_sla (hero card only)
 * @returns React Query result with consultation data and loading states
 *
 * @example
 * // Get latest active consultation for appointment display
 * const { data: consult, isLoading } = useLatestConsultRealtime();
 *
 * @example
 * // Get latest video consultation including canceled ones for reschedule logic
 * const { data: videoConsult } = useLatestConsultRealtime(
 *   [ConsultType.scheduled_video_visit, ConsultType.followup_video_visit],
 *   true
 * );
 *
 * @example
 * // Get latest async consultation for medication order status
 * const { data: asyncConsult } = useLatestConsultRealtime([ConsultType.initial_async]);
 */
export const useLatestConsultRealtime = (
  consultType = [ConsultType.scheduled_video_visit],
  includeCanceled = false,
  includeRejectedInitialAsyncMissedSla = false
) => {
  const queryKey = useMemo(
    () =>
      JSON.stringify([
        "latest consult",
        { consultType, includeCanceled, includeRejectedInitialAsyncMissedSla },
      ]),
    [consultType, includeCanceled, includeRejectedInitialAsyncMissedSla]
  );

  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const latestUnsub = useRef<(() => void) | undefined>(undefined);

  // Query for latest consult document
  const queryResult = useQuery({
    queryKey: JSON.parse(queryKey), // Parse back to an object/array
    queryFn: () =>
      fetchLatestConsult(
        currentUser?.uid as string,
        consultType,
        includeCanceled,
        includeRejectedInitialAsyncMissedSla
      ),
    enabled: !!currentUser?.uid,
  });

  const consultGuid = queryResult.data?.guid;

  useEffect(() => {
    if (!consultGuid) return;

    // Unsubscribe from any existing subscription
    if (latestUnsub.current) {
      latestUnsub.current();
    }

    // Subscribe to real-time updates on document
    latestUnsub.current = onSnapshot(
      doc(db, "steadymd.consults", consultGuid),
      (docSnapshot) => {
        const newData = docSnapshot.data();
        const currentData = queryClient.getQueryData(JSON.parse(queryKey));

        if (includeCanceled) {
          // For includeCanceled queries, update cache with any status change
          if (
            newData &&
            JSON.stringify(newData) !== JSON.stringify(currentData)
          ) {
            queryClient.setQueryData(JSON.parse(queryKey), newData);
          }
        } else {
          // If the consult was canceled, invalidate the query to fetch a new non-canceled one
          if (newData?.visitStatus === ConsultVisitStatusEnum.Canceled) {
            queryClient.invalidateQueries({ queryKey: JSON.parse(queryKey) });
            return;
          }

          const reasonMatch = isMissedSlaReason(newData as SMDConsult);
          const isAllowedRejected =
            includeRejectedInitialAsyncMissedSla &&
            newData?.consultType === ConsultType.initial_async &&
            newData?.visitStatus === ConsultVisitStatusEnum.Rejected &&
            reasonMatch;

          // Rejected but not in allowed-rejected case: invalidate so we refetch and get next latest (or null)
          if (
            newData?.visitStatus === ConsultVisitStatusEnum.Rejected &&
            !isAllowedRejected
          ) {
            queryClient.invalidateQueries({ queryKey: JSON.parse(queryKey) });
            return;
          }

          // Update cache when data changed and (not canceled, or allowed rejected missed_sla)
          const shouldUpdate =
            newData &&
            newData.visitStatus !== ConsultVisitStatusEnum.Canceled &&
            (newData.visitStatus !== ConsultVisitStatusEnum.Rejected ||
              isAllowedRejected) &&
            JSON.stringify(newData) !== JSON.stringify(currentData);
          if (shouldUpdate) {
            queryClient.setQueryData(JSON.parse(queryKey), newData);
          }
        }
      },
      (error) => {
        console.error("Error fetching steadymd consults: ", error);
      }
    );

    // Clean up subscription on unmount or consultGuid change
    return () => {
      if (latestUnsub.current) {
        latestUnsub.current();
        latestUnsub.current = undefined;
      }
    };
  }, [
    consultGuid,
    queryKey,
    queryClient,
    includeCanceled,
    includeRejectedInitialAsyncMissedSla,
  ]);

  useIonViewDidLeave(() => {
    if (latestUnsub.current) {
      latestUnsub.current();
      latestUnsub.current = undefined;
    }
  });

  return queryResult;
};
